import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { deleteConfirmation, type DeleteAudience } from '../../lib/portalAssets/deletion';
import type { PortalAssetTile } from '../../types/portalAsset';

// DELETE ONE ASSET, WITH A CONFIRMATION THAT SAYS WHAT DELETE MEANS (2026-10-08).
// Shared by the client page and the staff dialog; the host supplies the call.
//
// ⚠️ INLINE, NOT A DIALOG: a portaled dialog renders outside `.kyck-portal` and
// takes the STAFF tokens (CLAUDE.md, the portaled-content note), so on the
// client page it would be a white box on a dark report.
//
// ⚠️ Every sentence comes from the pure deleteConfirmation (deletion.ts) — the
// approved-asset warning and, for staff, that the FILE IS NOT REMOVED. A
// confirmation that let someone believe a delete was a takedown would be worse
// than no confirmation.

export function DeleteAssetControl({
  tile,
  audience,
  onDelete,
  pending,
  error,
}: {
  tile: Pick<PortalAssetTile, 'title' | 'originalFilename' | 'review' | 'deletedDaysLeft'>;
  audience: DeleteAudience;
  onDelete: () => void;
  pending: boolean;
  error: string | null;
}) {
  const [confirming, setConfirming] = useState(false);
  if (tile.deletedDaysLeft !== null) return null;
  const c = deleteConfirmation(tile, audience);

  if (!confirming) {
    return (
      <Button size="sm" variant="outline" onClick={() => setConfirming(true)}>
        Delete…
      </Button>
    );
  }
  return (
    <div className="space-y-2 rounded-md border border-border p-3 text-sm" role="group" aria-label="Confirm delete">
      <p className="break-all font-medium text-foreground">{c.title}</p>
      {c.file && (
        <p className="break-all text-muted-foreground" data-testid="confirm-original-file">
          {c.file}
        </p>
      )}
      {c.warning && (
        <p className="font-medium text-foreground" data-testid="asset-delete-approved-warning">
          {c.warning}
        </p>
      )}
      {c.body.map((line) => (
        <p key={line} className="text-muted-foreground">
          {line}
        </p>
      ))}
      <div className="flex gap-2">
        <Button size="sm" variant="destructive" disabled={pending} onClick={onDelete}>
          {pending ? 'Deleting…' : 'Delete'}
        </Button>
        <Button size="sm" variant="ghost" disabled={pending} onClick={() => setConfirming(false)}>
          Cancel
        </Button>
      </div>
      {error && (
        <p className="text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
